/**
 * Shared HTTP client for `/api/v1/*` in the web app.
 * Prefer `apiGet` / `apiPost` / `apiPatch` / `apiDelete` from domain `*Api` modules under `hooks/`.
 */

import { isSignOutApiBlocked } from "@sukha/query/signOutGate";
import { getApiToken } from "@/lib/api/authToken";
import { resolveApiBaseUrl } from "@/lib/api/baseUrl";
import { tagServiceUnreachable } from "@/lib/errors/serviceUnreachable";
import { isMaintenanceExemptPath } from "@sukha/shared/lib/maintenanceAccess";

/**
 * Where `/api/v1/*` actually lives, for this caller.
 *
 * The browser used a relative URL — `""`, the page's own origin — which meant
 * every API call in the product went to the web app and was rewritten to
 * `apps/api` from there. One extra hop per request, and one request's logs and
 * traces split across two Vercel projects, which is the part that hurts when
 * something breaks (M4 of `docs/plans/web-api-extraction.md`).
 *
 * `NEXT_PUBLIC_API_ORIGIN` sends the browser straight there instead.
 *
 * **Unset means unchanged.** This is config-gated rather than a code flip so the
 * move is reversible by an environment variable rather than a deploy: today
 * nothing sets it, the browser keeps its relative URL, and the rewrite keeps
 * serving. That also matters for order of operations — the API's CORS allowlist
 * (`apps/api/src/cors.ts`) has to know about an origin BEFORE that origin starts
 * calling it, and a variable can be set after the API is deployed rather than
 * with it.
 *
 * Going direct is only possible because web already sends a Bearer token
 * (TX-4856). A cookie cannot cross an origin; that was the whole blocker.
 */
/**
 * Delegates to the one implementation in `lib/api/baseUrl`.
 *
 * Takes the path because not everything moved: a route still served by web must
 * stay on the web origin even when the browser talks to the API directly.
 */
export function getApiBaseUrl(path?: string): string {
  return resolveApiBaseUrl(path);
}

/**
 * A path turned into the URL that actually reaches the service that owns it.
 *
 * `getApiBaseUrl` has existed and worked for a while; what was missing was
 * anything making it convenient at a bare `fetch`. So 83 call sites across 50
 * files kept their relative path, stayed same-origin, and took the web rewrite
 * — the browser-goes-direct work was built, configured and then almost entirely
 * unused, because using it meant remembering to.
 *
 * Deliberately returns a URL rather than performing the request. `apiRequest`
 * is the richer client, but it also owns error handling, and moving 83 call
 * sites onto it would rewrite the error path of payments, onboarding and the
 * whole admin surface in one diff. This changes the origin and NOTHING else:
 * every caller keeps its own `response.ok`, `.status` and `.json()` exactly as
 * written.
 *
 * Safe for a path that has not moved — `getApiBaseUrl` returns `""` for one the
 * web app still serves, so the call stays same-origin without the caller
 * knowing which is which.
 */
export function apiUrl(path: string): string {
  return `${getApiBaseUrl(path)}${path}`;
}

export interface ApiErrorBody {
  message?: string;
  code?: string;
  [key: string]: unknown;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code?: string;
  readonly body: ApiErrorBody | null;

  constructor(
    message: string,
    options: { status: number; code?: string; body?: ApiErrorBody | null },
  ) {
    super(message);
    this.name = "ApiError";
    this.status = options.status;
    this.code = options.code;
    this.body = options.body ?? null;
  }
}

export interface ApiClientOptions {
  /** Clerk or custom bearer token for server-side calls */
  token?: string;
  signal?: AbortSignal;
  /** Merge into init.headers */
  headers?: HeadersInit;
  /**
   * Let the request outlive the page. For the last word a closing tab sends:
   * a normal request is cancelled as the page is torn down.
   */
  keepalive?: boolean;
}

function buildHeaders(
  token: string | undefined,
  extra?: HeadersInit,
): HeadersInit {
  const h = new Headers(extra ?? {});
  if (!h.has("Content-Type")) {
    h.set("Content-Type", "application/json");
  }
  if (token) {
    h.set("Authorization", `Bearer ${token}`);
  }
  return h;
}

async function parseJsonBody(response: Response): Promise<unknown> {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return text;
  }
}

/**
 * A member whose tab was already open when maintenance started would otherwise
 * grind through failing request after failing request. One reload puts them on
 * the middleware's maintenance page instead.
 *
 * Module-level guard: a dashboard has many requests in flight, and they would
 * all try to reload. No polling and no client-side overlay — the middleware
 * response stays the only rendering of maintenance, and a dashboard talks to
 * the API often enough that this fires within seconds. A genuinely idle tab
 * stays as it is until the member touches something, which is the moment it
 * matters.
 *
 * The guard cannot be the only thing stopping a second reload, because a reload
 * resets it. What stops a *loop* is the page the member lands on: for a member
 * that is the maintenance page, which runs no JavaScript. An exempt page is the
 * opposite case — `/admin` stays up and keeps fetching non-exempt endpoints
 * (`/api/v1/payment/trial`, `/api/v1/settings/version`, ...), every one of them
 * a 503, so reloading there loops forever and locks admins out of the panel
 * during the window it exists to let them work in. Exempt page, no reload.
 */
let maintenanceReloadRequested = false;

function reloadIfMaintenance(body: ApiErrorBody | null): void {
  if (maintenanceReloadRequested) return;
  if (typeof window === "undefined") return;
  if (body?.error !== "maintenance") return;
  if (isMaintenanceExemptPath(window.location.pathname)) return;
  maintenanceReloadRequested = true;
  window.location.reload();
}

/**
 * Low-level request helper. Prefer `apiGet` / `apiPost` / `apiPatch` / `apiDelete`.
 */
export async function apiRequest<T>(
  path: string,
  init: RequestInit = {},
  options: ApiClientOptions = {},
): Promise<T> {
  if (isSignOutApiBlocked()) {
    throw new DOMException("Sign-out in progress", "AbortError");
  }

  const baseUrl = getApiBaseUrl(path);
  const url = path.startsWith("http") ? path : `${baseUrl}${path}`;

  // An explicit token wins — server-side callers pass their own. Otherwise ask
  // the registry, which the browser's `ApiTokenProvider` fills from Clerk.
  const token = options.token ?? (await getApiToken()) ?? undefined;

  const send = (bearer: string | undefined): Promise<Response> =>
    fetch(url, {
      ...init,
      ...(options.keepalive ? { keepalive: true } : {}),
      signal: options.signal ?? init.signal,
      headers: buildHeaders(bearer, init.headers as HeadersInit | undefined),
    }).catch((error: unknown) => {
      // A server render that cannot reach the API: tag it so the client boundary
      // can still tell after production scrubs the message (TX-5201).
      throw typeof window === "undefined" ? tagServiceUnreachable(error) : error;
    });

  let response = await send(token);

  /*
   * One retry with a fresh token, and only on a 401.
   *
   * A Clerk session token lives about a minute, and `getToken()` serves a
   * cached one. A tab left idle past expiry would otherwise get a 401 that
   * every caller surfaces as an error — and the member reads that as being
   * signed out when they are not.
   *
   * Deliberately narrow: only 401, only when a token was actually sent, only
   * when the retry produces a DIFFERENT token, and only once. Retrying without
   * those conditions turns a genuine authorization failure into two requests
   * and hides the real status behind the second one.
   */
  if (response.status === 401 && token && !options.token) {
    const refreshed = await getApiToken({ skipCache: true });
    if (refreshed && refreshed !== token) {
      response = await send(refreshed);
    }
  }

  if (!response.ok) {
    const parsed = await parseJsonBody(response);
    const body =
      parsed && typeof parsed === "object"
        ? (parsed as ApiErrorBody)
        : null;
    if (response.status === 503) {
      reloadIfMaintenance(body);
    }
    const messageFromJson =
      body && typeof body.message === "string" ? body.message : undefined;
    const message =
      messageFromJson ||
      `Request failed: ${response.status} ${response.statusText}`;
    const code =
      body && typeof body.code === "string" ? body.code : undefined;
    throw new ApiError(message, {
      status: response.status,
      code,
      body,
    });
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const text = await response.text();
  if (!text) {
    return undefined as T;
  }

  const contentType = response.headers.get("content-type");
  if (contentType?.includes("application/json")) {
    return JSON.parse(text) as T;
  }

  return text as unknown as T;
}

export function apiGet<T>(
  path: string,
  options: ApiClientOptions = {},
): Promise<T> {
  return apiRequest<T>(path, { method: "GET" }, options);
}

export function apiPost<T>(
  path: string,
  body: unknown,
  options: ApiClientOptions = {},
): Promise<T> {
  return apiRequest<T>(
    path,
    {
      method: "POST",
      body: body === undefined ? undefined : JSON.stringify(body),
    },
    options,
  );
}

export function apiPatch<T>(
  path: string,
  body: unknown,
  options: ApiClientOptions = {},
): Promise<T> {
  return apiRequest<T>(
    path,
    {
      method: "PATCH",
      body: body === undefined ? undefined : JSON.stringify(body),
    },
    options,
  );
}

export function apiPut<T>(
  path: string,
  body: unknown,
  options: ApiClientOptions = {},
): Promise<T> {
  return apiRequest<T>(
    path,
    {
      method: "PUT",
      body: body === undefined ? undefined : JSON.stringify(body),
    },
    options,
  );
}

export function apiDelete<T>(
  path: string,
  options: ApiClientOptions = {},
): Promise<T> {
  return apiRequest<T>(path, { method: "DELETE" }, options);
}
